/**
 * PropertyBackgroundSync
 *
 * Keeps PropertyListStore list and schema cache warm for the active customer so
 * property-related pages feel snappy. Runs on the main thread (no Web Worker)
 * because stores use MobX and AWS Amplify, which must run on the main thread.
 */

const SYNC_INTERVAL_MS = 2 * 60 * 1000;
/** Spacing between non-forced syncs (no server-side rate limit; avoids bursts on tab churn). */
const MIN_SYNC_SPACING_MS = 10_000;

let _store = null;
let _activeCustomer = null;
let _activeUserId = null;
let _activeRole = null;
let _intervalId = null;
let _lastSyncSpacingAt = 0;

function _sync(force = false) {
  const listStore = _store?.propertyListStore;
  if (!listStore || !_activeCustomer || typeof _activeCustomer !== "string") return;
  const customer = _activeCustomer.trim();
  if (!customer) return;

  const t = Date.now();
  if (!force && t - _lastSyncSpacingAt < MIN_SYNC_SPACING_MS) return;
  _lastSyncSpacingAt = t;

  listStore.loadList({
    customer,
    userId: _activeUserId,
    role: _activeRole,
    revalidateAfterMs: listStore.defaultListRevalidateAfterMs,
    textSearchQuery: listStore.listTextSearchQuery,
  });
  listStore.loadSchema(customer);
}

function runWhenVisible() {
  if (typeof document !== "undefined" && document.visibilityState === "visible" && _activeCustomer) {
    _sync(false);
  }
}

function start(store) {
  if (!store?.propertyListStore) return;
  _store = store;
  if (_intervalId != null) return;

  _intervalId = setInterval(runWhenVisible, SYNC_INTERVAL_MS);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", _onVisibilityChange);
    document.addEventListener("pagehide", _onPageHide);
  }
}

function _onPageHide() {
  if (_intervalId != null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
}

function stop() {
  if (_intervalId != null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", _onVisibilityChange);
    document.removeEventListener("pagehide", _onPageHide);
  }
  _activeCustomer = null;
  _activeUserId = null;
  _activeRole = null;
  _store = null;
}

function _onVisibilityChange() {
  if (typeof document === "undefined") return;
  if (document.visibilityState === "hidden") {
    if (_intervalId != null) {
      clearInterval(_intervalId);
      _intervalId = null;
    }
    return;
  }
  if (document.visibilityState === "visible") {
    if (_intervalId == null) {
      _intervalId = setInterval(runWhenVisible, SYNC_INTERVAL_MS);
    }
    _sync(true);
  }
}

/**
 * @param {string|null|undefined} customerId
 * @param {string|null|undefined} [userId] - Effective auth user; forwarded to loadList for per-user cache keys.
 * @param {string|null|undefined} [role] - Active view hat; forwarded as fetchProperties `user_role` and gates server text search.
 */
function setActiveCustomer(customerId, userId = null, role = null) {
  const next = customerId == null || customerId === "" ? null : String(customerId).trim();
  const nextUserId = userId == null || userId === "" ? null : String(userId).trim();
  const nextRole = role == null || role === "" ? null : String(role).trim();
  const isCustomerSwitch = Boolean(next) && Boolean(_activeCustomer) && next !== _activeCustomer;
  if (isCustomerSwitch && _store?.propertyListStore?.resetForNewCustomer) {
    _store.propertyListStore.resetForNewCustomer();
  }
  _activeCustomer = next;
  _activeUserId = nextUserId;
  _activeRole = nextRole;
  if (next) _sync(true);
}

function runInitialSync() {
  _sync(true);
}

const PropertyBackgroundSync = {
  start,
  stop,
  setActiveCustomer,
  runInitialSync,
};

export default PropertyBackgroundSync;
